/**
 * API Client Library
 * Replaces Supabase client with PHP REST API calls
 */

// Use environment variable or default to /api (relative path works on same server)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

console.log('===========================================');
console.log('🔧 API Config - Final URL:', API_BASE_URL);
console.log('🔧 API Config - Window location:', window.location.href);
console.log('===========================================');

// Expose for debugging
window.__ERP_API_URL__ = API_BASE_URL;


class ApiClient {
    constructor() {
        this.baseURL = API_BASE_URL;
        console.log('🚀 API Client Initialized with URL:', this.baseURL);
        console.log('🔍 Window location:', window.location.host, window.location.pathname);
        // Use erp_auth_token to match www auth system
        this.token = localStorage.getItem('erp_auth_token');
    }

    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('erp_auth_token', token);
        } else {
            localStorage.removeItem('erp_auth_token');
        }
    }

    getToken() {
        // Use erp_auth_token to match www auth system
        return this.token || localStorage.getItem('erp_auth_token');
    }

   async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const token = this.getToken();
        const isFormData = options.body instanceof FormData;

        const config = {
            headers: {
                ...(!isFormData && { 'Content-Type': 'application/json' }),
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
            ...options,
        };

        if (isFormData) {
            config.body = options.body; // send FormData as-is, let browser set boundary
        } else if (options.body && typeof options.body === 'object') {
            config.body = JSON.stringify(options.body);
        }

        try {
            const response = await fetch(url, config);

            // Handle 401 Unauthorized - clear token and redirect to login
            const hasLocalAuth = !!localStorage.getItem('laborpro_local_auth');
            if (response.status === 401 && endpoint !== '/auth/login' && endpoint !== '/auth/register' && !hasLocalAuth) {
                this.setToken(null);
                // Only redirect if we're not already on the login page
                const currentPath = window.location.pathname;
                if (currentPath !== '/erp/login' && currentPath !== '/erp/login-authentication') {
                    window.location.href = '/erp/login';
                }
                throw new Error('Session expired. Please login again.');
            }

            // Get response text - read directly without cloning to avoid issues
            let responseText = '';
            try {
                responseText = await response.text();
            } catch (textError) {
                responseText = 'Failed to read response body: ' + (textError.message || 'Unknown error');
            }
            
            // Try to parse as JSON
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                // If not valid JSON, create error response
                data = {
                    success: false,
                    error: response.ok ? 'Invalid JSON response from server' : 'Non-JSON response from server',
                    message: responseText.substring(0, 200)
                };
            }

            if (!response.ok) {
                // Safely extract error message
                const errorMsg = data.error || data.message || 'Request failed';
                const safeErrorMsg = typeof errorMsg === 'string' ? errorMsg : String(errorMsg);
                throw new Error(safeErrorMsg);
            }

            return data;
        } catch (error) {
            if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
                throw new Error('Cannot connect to API server. Please check if the backend is running.');
            }
            // Ensure error message is safe
            if (error && error.message) {
                throw new Error(String(error.message));
            }
            throw new Error('An unexpected error occurred');
        }
    }

    // Auth methods
    async signIn(email, password) {
        const data = await this.request('/auth/login', {
            method: 'POST',
            body: { email, password },
        });

        // Handle different response formats
        // Root API returns: { success: true, data: { id, name, email, ... } }
        // ERP backend returns: { success: true, data: { session: { access_token }, user: { ... } } }
        let accessToken = null;
        let userProfile = null;
        
        if (data.data?.session?.access_token) {
            // ERP backend format
            accessToken = data.data.session.access_token;
            userProfile = data.data?.userProfile || data.data?.user;
        } else if (data.data?.id) {
            // Root API format - use user id as token
            accessToken = data.data.id?.toString();
            userProfile = data.data;
        }
        
        if (accessToken) {
            this.setToken(accessToken);
        }

        // Calculate dashboard route based on role
        let dashboardRoute = '/erp/dashboard-overview'; // default

        if (userProfile?.role) {
            switch (userProfile.role) {
                case 'super_admin':
                    dashboardRoute = '/erp/dashboard-overview';
                    break;
                case 'hr_admin':
                    dashboardRoute = '/erp/employee-management';
                    break;
                case 'telecaller':
                    dashboardRoute = '/erp/telecaller-dashboard';
                    break;
                case 'staff':
                    dashboardRoute = '/erp/client-requirements-matching';
                    break;
                case 'client':
                    dashboardRoute = '/erp/invoice-billing-management';
                    break;
                case 'employee':
                    dashboardRoute = '/erp/employee-self-service';
                    break;
                default:
                    dashboardRoute = '/erp/dashboard-overview';
            }
        } else if (userProfile?.company_name) {
            // If user has company_name, they're a client
            dashboardRoute = '/erp/invoice-billing-management';
        }

        return {
            success: data.success,
            data: {
                session: {
                    user: userProfile,
                    access_token: accessToken,
                },
            },
            userProfile: userProfile,
            dashboardRoute: dashboardRoute,
            error: data.error,
        };
    }

    async getSession() {
        try {
            const data = await this.request('/auth/session');
            return {
                success: data.success,
                data: {
                    session: {
                        user: data.data?.session?.user,
                    },
                },
            };
        } catch (error) {
            // If session check fails with 401, user is not authenticated
            if (error.message.includes('Session expired') || error.message.includes('Authentication required')) {
                this.setToken(null);
            }
            return {
                success: false,
                data: { session: null },
                error: error.message,
            };
        }
    }

    async getUserProfile(userId) {
        try {
            const data = await this.request(`/auth/profile${userId ? `/${userId}` : ''}`);
            return {
                success: data.success,
                data: data.data,
            };
        } catch (error) {
            return {
                success: false,
                error: error.message,
            };
        }
    }

    async signOut() {
        try {
            await this.request('/auth/logout', { method: 'POST' });
            this.setToken(null);
            return { success: true };
        } catch (error) {
            this.setToken(null);
            return { success: true }; // Always succeed on logout
        }
    }

    async resetPassword(email) {
        const data = await this.request('/auth/resetPassword', {
            method: 'POST',
            body: { email },
        });
        return {
            success: data.success,
            error: data.error,
        };
    }

    // Generic CRUD methods
    async get(endpoint, params = {}) {
        try {
            // Strip undefined, null, and empty string values to prevent "key=undefined" query strings
            const cleanParams = {};
            if (params && typeof params === 'object') {
                Object.keys(params).forEach(key => {
                    const val = params[key];
                    if (val !== undefined && val !== null && val !== '') {
                        cleanParams[key] = val;
                    }
                });
            }
            const queryString = new URLSearchParams(cleanParams).toString();
            const url = queryString ? `${endpoint}?${queryString}` : endpoint;
            const data = await this.request(url, { method: 'GET' });
            return {
                success: data.success,
                data: data.data,
                error: data.error,
            };
        } catch (error) {
            // Safely extract error message
            let errorMessage = 'Request failed';
            if (error && typeof error === 'object') {
                errorMessage = error.message || error.error || String(error);
            } else if (error) {
                errorMessage = String(error);
            }
            return {
                success: false,
                data: null,
                error: errorMessage,
            };
        }
    }

    async post(endpoint, body) {
        const data = await this.request(endpoint, {
            method: 'POST',
            body,
        });
        return {
            success: data.success,
            data: data.data,
            error: data.error,
        };
    }

    async put(endpoint, body) {
        const data = await this.request(endpoint, {
            method: 'PUT',
            body,
        });
        return {
            success: data.success,
            data: data.data,
            error: data.error,
        };
    }

    async delete(endpoint) {
        const data = await this.request(endpoint, {
            method: 'DELETE',
        });
        return {
            success: data.success,
            error: data.error,
        };
    }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;

